import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { BrandMark } from '../components/BrandMark.tsx';
import { Icon, type IconName } from '../components/Icon.tsx';
import { Button } from '../components/ui.tsx';
import { t } from '../i18n/index.ts';
import { ApiError, request } from '../lib/api.ts';
import { useSession } from '../lib/session.tsx';

/**
 * The "Allow" page an app sends a person to (OAuth 2.0): who the app is, what it asks for, and two
 * buttons. The answer sends the browser back to the app, with a code or with a "no".
 */

interface Info {
  app: { id: string; name: string; description: string | null; homepage: string | null; made_by: string | null; public: boolean };
  business: string;
  access: 'read' | 'write';
  modules: { name: string; label: string }[] | null;
  redirect_host: string;
  connected: boolean;
}
type Failure = { message: string; otherBusiness?: string; needsMfa?: boolean };

const KEYS = ['response_type', 'client_id', 'redirect_uri', 'scope', 'state', 'code_challenge', 'code_challenge_method'] as const;

export function Authorize() {
  const { me, signOut } = useSession();
  const query = useMemo(() => {
    const p = new URLSearchParams(window.location.search);
    const q: Record<string, string> = {};
    for (const k of KEYS) {
      const v = p.get(k);
      if (v !== null) q[k] = v;
    }
    return q;
  }, []);
  const [info, setInfo] = useState<Info | null>(null);
  const [failed, setFailed] = useState<Failure | null>(null);
  const [busy, setBusy] = useState<'allow' | 'deny' | null>(null);
  const [leaving, setLeaving] = useState(false);

  const fail = (err: unknown) => {
    if (err instanceof ApiError && err.code !== 'offline') {
      const d = (err.details ?? {}) as { other_business?: string; needs_mfa?: boolean };
      setFailed({ message: err.message, otherBusiness: d.other_business, needsMfa: d.needs_mfa });
    } else setFailed({ message: t('error.offline') });
  };
  const go = (to: string) => {
    setLeaving(true);
    window.location.replace(to);
  };

  useEffect(() => {
    request<Info | { redirect: string }>('POST', '/api/oauth/authorize/check', { query }).then((r) => ('redirect' in r ? go(r.redirect) : setInfo(r)), fail);
  }, [query]);

  const answer = (allow: boolean) => {
    setBusy(allow ? 'allow' : 'deny');
    request<{ redirect: string }>('POST', '/api/oauth/authorize', { query, allow }).then(
      (r) => go(r.redirect),
      (err) => {
        setBusy(null);
        fail(err);
      },
    );
  };

  return (
    <main className="oauth-page">
      <section className="card oauth-card" aria-busy={!info && !failed} aria-live="polite">
        <span className="brand-mark">
          <span className="brand-logo cube">
            <BrandMark size={26} />
          </span>
          <span className="brand-name">{t('app.name')}</span>
        </span>
        {failed ? (
          <>
            <span className="oauth-mark oauth-mark-warn" aria-hidden="true">
              <Icon name="alert" size={26} />
            </span>
            <h1>{t('oauth.failed')}</h1>
            <p className="form-error" role="alert">
              {failed.message}
            </p>
            <div className="oauth-actions">
              {failed.otherBusiness ? (
                <Button variant="primary" icon="logout" onClick={() => void signOut()}>
                  {t('oauth.switch')}
                </Button>
              ) : failed.needsMfa ? (
                <Button variant="primary" icon="shield" onClick={() => window.location.assign('/security')}>
                  {t('oauth.set_up')}
                </Button>
              ) : null}
              <Button variant="secondary" onClick={() => window.location.assign('/')}>
                {t('oauth.home')}
              </Button>
            </div>
          </>
        ) : !info || leaving ? (
          <div className="oauth-wait">
            <span className="spinner" aria-hidden="true" />
            <p className="muted">{leaving ? t('oauth.leaving') : t('oauth.checking')}</p>
          </div>
        ) : (
          <>
            <div className="oauth-pair" aria-hidden="true">
              <span className="oauth-app-mark">{info.app.name.slice(0, 1).toUpperCase()}</span>
              <span className="oauth-link">
                <Icon name="transfer" size={18} />
              </span>
              <span className="brand-logo cube">
                <BrandMark size={26} />
              </span>
            </div>
            <h1>{t('oauth.title', { app: info.app.name })}</h1>
            <p className="muted oauth-who">{t('oauth.who', { name: me?.name ?? '', business: info.business })}</p>
            {info.app.description ? <p className="oauth-desc">{info.app.description}</p> : null}
            <ul className="oauth-asks">
              <Ask icon="eye">{info.modules ? t('oauth.see_in', { parts: info.modules.map((m) => m.label).join(', ') }) : t('oauth.see_all')}</Ask>
              {info.access === 'write' ? <Ask icon="edit">{t('oauth.change')}</Ask> : <Ask icon="lock">{t('oauth.read_only')}</Ask>}
              <Ask icon="shield">{t('oauth.never')}</Ask>
              <Ask icon="clock">{t('oauth.until')}</Ask>
            </ul>
            {info.connected ? (
              <p className="callout callout-info">
                <Icon name="checkCircle" size={18} />
                <span>{t('oauth.already')}</span>
              </p>
            ) : null}
            <p className="muted small oauth-made">
              {info.app.made_by ? t('oauth.made_by', { name: info.app.made_by }) : t('oauth.made')}
              {info.app.homepage ? (
                <>
                  {' · '}
                  <a href={info.app.homepage} target="_blank" rel="noreferrer noopener">
                    {safeHost(info.app.homepage)}
                  </a>
                </>
              ) : null}
            </p>
            <div className="oauth-actions">
              <Button variant="secondary" busy={busy === 'deny'} disabled={!!busy} onClick={() => answer(false)}>
                {t('oauth.deny')}
              </Button>
              <Button variant="primary" icon="check" busy={busy === 'allow'} disabled={!!busy} onClick={() => answer(true)}>
                {t('oauth.allow')}
              </Button>
            </div>
            <p className="muted small oauth-then">{t('oauth.then', { host: info.redirect_host })}</p>
          </>
        )}
      </section>
    </main>
  );
}

function Ask({ icon, children }: { icon: IconName; children: ReactNode }) {
  return (
    <li>
      <Icon name={icon} size={18} />
      <span>{children}</span>
    </li>
  );
}

function safeHost(url: string) {
  try {
    return new URL(url).host;
  } catch {
    return url;
  }
}
